import { Outlet, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import {
  HomeIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChartBarIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  BeakerIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { useState, useRef, useEffect } from 'react'
import LanguageToggle from './LanguageToggle'

function Layout() {
  const { t } = useTranslation('app')
  const { user, team, teams, selectTeam, logout, canManageTeam, canAccessCampaigns, canAccessDebriefs, refreshTeam } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false)
  const orgDropdownRef = useRef(null)

  // Refresh team data on route changes to pick up admin changes (e.g., demo mode toggle)
  useEffect(() => {
    if (team) {
      refreshTeam()
    }
  }, [location.pathname])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setOrgDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Build navigation based on feature access
  const navigation = [
    // Campaigns - only show if user has access
    ...(canAccessCampaigns ? [
      { name: t('sidebar.campaigns'), href: '/app/campaigns', icon: HomeIcon },
    ] : []),
    // Daily Pulse/Debriefs - only show if user has access AND can manage team
    ...(canAccessDebriefs && canManageTeam ? [
      { name: t('sidebar.dailyPulse'), href: '/app/debrief', icon: ChartBarIcon },
    ] : []),
    { name: t('sidebar.partner', 'Partner'), href: '/app/partner', icon: BuildingOfficeIcon },
    { name: t('sidebar.settings', 'Settings'), href: '/app/settings', icon: Cog6ToothIcon },
    // Admin - only show for superadmins
    ...(user?.is_superadmin ? [
      { name: t('sidebar.admin', 'Admin'), href: '/app/admin', icon: ShieldCheckIcon },
    ] : []),
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center px-6 border-b">
          <Link to="/app" className="flex items-center space-x-3">
            <img src="/favicon.svg" alt="OrgPulse" className="w-8 h-8" />
            <div className="flex items-baseline">
              <span className="text-xl font-semibold text-slate-900">Org</span>
              <span className="text-xl font-semibold text-primary-600">Pulse</span>
              <span className="text-sm font-semibold text-slate-400 ml-1 uppercase tracking-wide">AI</span>
            </div>
          </Link>
        </div>

        {/* Organization switcher */}
        {team && (
          <div className="px-4 py-3 border-b" ref={orgDropdownRef}>
            <p className="text-xs text-gray-500 mb-1 px-2">{t('sidebar.team')}</p>
            {teams.length > 1 ? (
              <div className="relative">
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="truncate">{team.name}</span>
                  <ChevronUpDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </button>
                {orgDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {teams.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => {
                          selectTeam(org)
                          setOrgDropdownOpen(false)
                          // Reload page to refresh data for new org
                          window.location.reload()
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="truncate">{org.name}</span>
                        {org.id === team.id && (
                          <CheckIcon className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-900 truncate px-2">{team.name}</p>
            )}
          </div>
        )}

        {/* Language Toggle */}
        <div className="px-6 py-2 border-b">
          <LanguageToggle />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1">
          {navigation.map((item) => {
            // Fix: Only highlight the most specific matching nav item
            // Check if pathname matches this item AND no other nav item is a more specific match
            const pathMatches = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
            const hasMoreSpecificMatch = navigation.some(
              nav => nav.href !== item.href &&
                     nav.href.startsWith(item.href) &&
                     (location.pathname === nav.href || location.pathname.startsWith(nav.href + '/'))
            )
            const isActive = pathMatches && !hasMoreSpecificMatch
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </Link>
            )
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {user?.name?.[0] || user?.email?.[0]?.toUpperCase()}
                </span>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title={t('sidebar.logout')}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Demo mode banner */}
        {team?.is_demo && (
          <div className="bg-primary-100 text-primary-800 text-center py-2 text-sm flex items-center justify-center gap-2">
            <BeakerIcon className="w-4 h-4" />
            <span className="font-medium">Demo Mode</span>
            <span className="hidden sm:inline">- AI-simulated interviews for demonstration purposes</span>
          </div>
        )}

        {/* Mobile header */}
        <header className="lg:hidden flex items-center h-16 px-4 bg-white border-b">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <img src="/favicon.svg" alt="OrgPulse" className="w-6 h-6 ml-4" />
          <div className="flex items-baseline ml-2">
            <span className="text-lg font-semibold text-slate-900">Org</span>
            <span className="text-lg font-semibold text-primary-600">Pulse</span>
            <span className="text-xs font-semibold text-slate-400 ml-1 uppercase tracking-wide">AI</span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout
